import type { Product } from '@/types/domain';

export function normalizeSearchText(value: unknown): string {
  return String(value ?? '')
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function productSearchRank(product: Product, rawQuery: string): number | null {
  const query = normalizeSearchText(rawQuery);
  if (!query) return 0;

  const name = normalizeSearchText(product.name);
  const nameWords = name.split(/[^a-z0-9]+/).filter(Boolean);
  const barcode = normalizeSearchText(product.barcode);
  const internalCode = normalizeSearchText(product.internalCode);
  const secondaryFields = [product.brand, product.category, product.supplier, product.description]
    .map(normalizeSearchText);
  const queryWords = query.split(' ').filter(Boolean);
  const allFields = [name, barcode, internalCode, ...secondaryFields];
  const everyWordMatches = queryWords.every((word) =>
    allFields.some((field) => field.includes(word)),
  );

  if (barcode === query || internalCode === query) return 0;
  if (name === query) return 1;
  if (name.startsWith(query)) return 2;
  if (name.includes(query)) return 3;
  if (queryWords.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)))) return 4;
  if (everyWordMatches) return 5;
  if (barcode.includes(query) || internalCode.includes(query)) return 6;
  if (secondaryFields.some((field) => field.includes(query))) return 7;
  return null;
}

export function compareProductSearchResults(a: Product, b: Product, query = ''): number {
  const aRank = productSearchRank(a, query) ?? Number.MAX_SAFE_INTEGER;
  const bRank = productSearchRank(b, query) ?? Number.MAX_SAFE_INTEGER;
  if (aRank !== bRank) return aRank - bRank;
  return a.name.localeCompare(b.name, 'es', { sensitivity: 'base', numeric: true });
}
